import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type Game, type PitchCount } from '../services/api';

@customElement('observer-view')
export class ObserverView extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  token = '';

  @state()
  private game: Game | null = null;

  @state()
  private pitches: PitchCount[] = [];

  @state()
  private loading = true;

  @state()
  private error = '';

  @state()
  private lastUpdated: Date | null = null;

  private refreshInterval: number | null = null;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadGameData();
    // Auto-refresh every 10 seconds
    this.refreshInterval = window.setInterval(() => this.loadGameData(), 10000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  private async loadGameData() {
    if (!this.token) {
      this.error = 'No observer token provided';
      this.loading = false;
      return;
    }

    try {
      const data = await api.observeGame(this.token);
      this.game = data.game;
      this.pitches = data.pitches || [];
      this.lastUpdated = new Date();
      this.error = '';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('expired')) {
        this.error = 'This observer link has expired. Please request a new link from the game host.';
      } else if (message.includes('invalid')) {
        this.error = 'Invalid observer link. Please check the link and try again.';
      } else {
        this.error = 'Unable to load game data. Please try again later.';
      }
    } finally {
      this.loading = false;
    }
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private getTotalPitches(): number {
    return this.pitches.reduce((sum, p) => sum + p.count, 0);
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex flex-col items-center justify-center py-16 space-y-4">
          <span class="loading loading-spinner loading-lg text-primary"></span>
          <p class="text-base-content/60">Loading game data...</p>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="max-w-lg mx-auto p-4">
          <div class="alert alert-error shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 class="font-bold">Unable to View Game</h3>
              <p class="text-sm">${this.error}</p>
            </div>
          </div>
        </div>
      `;
    }

    if (!this.game) {
      return html`
        <div class="alert alert-warning">Game data not available</div>
      `;
    }

    const activePitchers = this.pitches.filter(p => p.count > 0).sort((a, b) => b.count - a.count);

    return html`
      <div class="max-w-2xl mx-auto p-4 space-y-4">
        <!-- Observer mode banner -->
        <div class="alert alert-info shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
          </svg>
          <div class="flex-1">
            <h3 class="font-bold">Observer Mode</h3>
            <p class="text-sm">View-only access. Data refreshes automatically.</p>
          </div>
          <div class="text-xs opacity-70">
            ${this.lastUpdated ? `Updated: ${this.lastUpdated.toLocaleTimeString()}` : ''}
          </div>
        </div>

        <!-- Game info card -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <h2 class="card-title text-2xl">${this.game.name}</h2>

            <div class="flex flex-wrap gap-4 text-sm text-base-content/70">
              ${this.game.location ? html`
                <div class="flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  ${this.game.location}
                </div>
              ` : nothing}

              <div class="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                ${this.formatDate(this.game.game_date)}
              </div>
            </div>

            ${this.game.home_team_name || this.game.away_team_name ? html`
              <div class="flex items-center gap-2 mt-2">
                ${this.game.home_team_name ? html`
                  <span class="badge badge-primary">${this.game.home_team_name}</span>
                ` : nothing}
                ${this.game.home_team_name && this.game.away_team_name ? html`
                  <span class="text-base-content/50">vs</span>
                ` : nothing}
                ${this.game.away_team_name ? html`
                  <span class="badge badge-secondary">${this.game.away_team_name}</span>
                ` : nothing}
              </div>
            ` : nothing}
          </div>
        </div>

        <!-- Stats summary -->
        <div class="stats stats-horizontal shadow w-full">
          <div class="stat">
            <div class="stat-title">Total Pitches</div>
            <div class="stat-value text-primary">${this.getTotalPitches()}</div>
          </div>
          <div class="stat">
            <div class="stat-title">Pitchers</div>
            <div class="stat-value">${activePitchers.length}</div>
          </div>
        </div>

        <!-- Pitch counts table -->
        <div class="card bg-base-100 shadow-xl">
          <div class="card-body">
            <h3 class="card-title text-lg">Pitch Counts</h3>

            ${activePitchers.length === 0 ? html`
              <p class="text-base-content/60 text-center py-8">No pitches recorded yet</p>
            ` : html`
              <div class="overflow-x-auto">
                <table class="table table-zebra">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th class="text-right">Pitches</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${activePitchers.map(p => html`
                      <tr>
                        <td>
                          <div class="flex items-center gap-2">
                            ${p.jersey_number !== undefined && p.jersey_number !== null ? html`
                              <span class="badge badge-outline badge-sm">#${p.jersey_number}</span>
                            ` : nothing}
                            <span class="font-medium">${p.player_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td class="text-right">
                          <span class="font-mono text-xl font-bold">${p.count}</span>
                        </td>
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
            `}
          </div>
        </div>

        <!-- Footer note -->
        <p class="text-center text-xs text-base-content/50">
          This is a read-only view. Contact the game host for editing access.
        </p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'observer-view': ObserverView;
  }
}
